import React, { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import useEscapeLayer from "../useEscape";

const MARGIN = 8;

/*
 * A floating panel pinned to the control that opened it.
 *
 * It renders where it is written (no portal), so a parent popover's own
 * "click outside closes me" still sees clicks in here as inside. It is placed
 * with position: fixed so no scrolling or overflow-hidden ancestor clips it,
 * flips above the anchor when there is no room below, and keeps inside the
 * window. Click away or Esc closes it; Esc hands focus back to the anchor.
 */
export default function Popover({ anchor, onClose, align = "left", gap = 6, className = "", label, children }) {
  const ref = useRef(null);

  const place = useCallback(() => {
    const a = anchor.current;
    const p = ref.current;
    if (!a || !p) return;
    const r = a.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;
    const vh = window.innerHeight;
    p.style.maxHeight = `${vh - MARGIN * 2}px`;
    const w = p.offsetWidth;
    const h = p.offsetHeight;
    let left = align === "right" ? r.right - w : r.left;
    left = Math.max(MARGIN, Math.min(left, vw - w - MARGIN));
    let top = r.bottom + gap;
    if (top + h > vh - MARGIN) top = r.top - gap - h >= MARGIN ? r.top - gap - h : Math.max(MARGIN, vh - MARGIN - h);
    p.style.top = `${top}px`;
    p.style.left = `${left}px`;
    // A transformed ancestor (a sliding panel, a dragged card) makes `fixed`
    // relative to itself rather than the window: measure where we landed and
    // shift by the difference.
    const got = p.getBoundingClientRect();
    if (Math.abs(got.top - top) > 0.5) p.style.top = `${2 * top - got.top}px`;
    if (Math.abs(got.left - left) > 0.5) p.style.left = `${2 * left - got.left}px`;
    p.style.visibility = "visible";
  }, [anchor, align, gap]);

  useLayoutEffect(place);

  useEffect(() => {
    const away = (e) => {
      if (ref.current && ref.current.contains(e.target)) return;
      if (anchor.current && anchor.current.contains(e.target)) return; // the anchor toggles itself
      onClose();
    };
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(place) : null;
    if (ro && ref.current) ro.observe(ref.current);
    document.addEventListener("mousedown", away);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      if (ro) ro.disconnect();
      document.removeEventListener("mousedown", away);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [anchor, onClose, place]);

  useEscapeLayer(true, () => {
    onClose();
    if (anchor.current) anchor.current.focus();
  });

  return (
    <div
      ref={ref}
      className={`dp-pop ${className}`}
      role="dialog"
      aria-label={label}
      style={{ visibility: "hidden" }}
      // Inside a <label>, a click on the panel's padding would otherwise be
      // forwarded to the labelled control and toggle the picker shut.
      onClick={(e) => e.preventDefault()}
    >
      {children}
    </div>
  );
}
